import { blobSchema, getBlobReadUrlResponseSchema, getBlobsResponseSchema, updateBatchBlobsResponseSchema, blobFilterCountSchema, getBlobNSCountResponseSchema, getBlobUniqueDocumentTypesResponseSchema, getBlobHistoryResponseSchema, blobNameListSchema, getBlobContractorsResponseSchema, getBlobReferenceCountResponseSchema, parsedDataResponseSchema, } from '@consigli/types';
import { createTransformer, createParams, mapToSnakeCase, } from './shared/base-api';
export const blobApiEndpoints = (builder, coreApiBaseUrl) => ({
    getBlob: builder.query({
        query: ({ projectId, packageId, blobId }) => `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/blobs/${blobId}/`,
        providesTags: ['Blob'],
        transformResponse: createTransformer(blobSchema, 'GetBlob'),
    }),
    getBlobs: builder.query({
        query: ({ projectId, packageId, page, pageSize, search, searchParsedData, status, suggestion, standard, documentType, blobIds, labelsReused, reference, }) => {
            const params = createParams({
                page,
                pageSize,
                search,
                searchParsedData,
                status,
                suggestion,
                standard,
                documentType,
                blobIds,
                labelsReused,
                reference,
            });
            return {
                url: `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/blobs/`,
                method: 'GET',
                params: mapToSnakeCase(params),
            };
        },
        providesTags: ['Blobs'],
        transformResponse: createTransformer(getBlobsResponseSchema, 'GetBlobs'),
    }),
    getBlobReadUrl: builder.query({
        query: ({ projectId, packageId, blobId }) => `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/blobs/${blobId}/read-url/`,
        transformResponse: createTransformer(getBlobReadUrlResponseSchema, 'GetBlobReadUrl'),
    }),
    createBlob: builder.mutation({
        query: ({ projectId, packageId, data }) => ({
            url: `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/blobs/`,
            method: 'POST',
            body: mapToSnakeCase(data),
        }),
        invalidatesTags: ['Blobs'],
        transformResponse: createTransformer(blobSchema, 'CreateBlob'),
    }),
    updateBlob: builder.mutation({
        query: ({ projectId, packageId, blobId, data }) => ({
            url: `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/blobs/${blobId}/`,
            method: 'PATCH',
            body: mapToSnakeCase(data),
        }),
        invalidatesTags: ['Blobs', 'NSCount', 'UniqueDocumentTypes', 'BlobNameList'],
        transformResponse: createTransformer(blobSchema, 'UpdateBlob'),
    }),
    overwriteBlobContent: builder.mutation({
        query: ({ projectId, packageId, blobId, content }) => ({
            url: `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/blobs/${blobId}/overwrite-content/`,
            method: 'POST',
            body: { content },
        }),
        invalidatesTags: ['Blobs'],
    }),
    updateBatchBlobs: builder.mutation({
        query: ({ projectId, packageId, data }) => ({
            url: `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/blobs/`,
            method: 'PATCH',
            body: data,
        }),
        invalidatesTags: ['Blobs', 'NSCount', 'UniqueDocumentTypes', 'BlobNameList'],
        transformResponse: createTransformer(updateBatchBlobsResponseSchema, 'UpdateBlobs'),
    }),
    deleteBlob: builder.mutation({
        query: ({ projectId, packageId, blobId }) => ({
            url: `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/blobs/${blobId}/`,
            method: 'DELETE',
        }),
        invalidatesTags: ['Blobs', 'Findings'],
    }),
    getBlobFilterCount: builder.query({
        query: ({ projectId, packageId, blobIds, searchParsedData, search }) => {
            const params = createParams({ blobIds, searchParsedData, search });
            return {
                url: `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/blobs/status-count/`,
                method: 'GET',
                params: mapToSnakeCase(params),
            };
        },
        providesTags: ['Blobs'],
        transformResponse: createTransformer(blobFilterCountSchema, 'BlobStatusCount'),
    }),
    getBlobsNSCount: builder.query({
        query: ({ projectId, packageId, documentType, searchParsedData, blobIds, search, status, labelsReused, }) => {
            const params = createParams({
                documentType,
                searchParsedData,
                blobIds,
                search,
                status,
                labelsReused,
            });
            return {
                url: `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/blobs/standard-count/`,
                method: 'GET',
                params: mapToSnakeCase(params),
            };
        },
        providesTags: ['NSCount'],
        transformResponse: createTransformer(getBlobNSCountResponseSchema, 'GetBlobNSCount'),
    }),
    getBlobUniqueDocumentTypes: builder.query({
        query: ({ projectId, packageId, standard, searchParsedData, documentType, blobIds, search, status, labelsReused, }) => {
            const params = createParams({
                standard,
                searchParsedData,
                documentType,
                blobIds,
                search,
                status,
                labelsReused,
            });
            return {
                url: `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/blobs/document-types/`,
                method: 'GET',
                params: mapToSnakeCase(params),
            };
        },
        providesTags: ['UniqueDocumentTypes'],
        transformResponse: createTransformer(getBlobUniqueDocumentTypesResponseSchema, 'UniqueDocumentTypes'),
    }),
    getBlobHistory: builder.query({
        query: ({ projectId, packageId, page }) => {
            const params = createParams({ page });
            return {
                url: `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/blobs/history/`,
                method: 'GET',
                params: mapToSnakeCase(params),
            };
        },
        transformResponse: createTransformer(getBlobHistoryResponseSchema, 'GetBlobHistory'),
    }),
    getBlobNameList: builder.query({
        query: ({ projectId, packageId, searchParsedData, status, standard, documentType }) => {
            const params = createParams({ searchParsedData, status, standard, documentType });
            return {
                url: `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/blobs/name-list/`,
                method: 'GET',
                params: mapToSnakeCase(params),
            };
        },
        providesTags: ['BlobNameList'],
        transformResponse: createTransformer(blobNameListSchema, 'GetBlobNameList'),
    }),
    getBlobContractors: builder.query({
        query: ({ projectId, packageId }) => `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/blobs/contractors/`,
        providesTags: ['Blobs'],
        transformResponse: createTransformer(getBlobContractorsResponseSchema, 'GetBlobContractors'),
    }),
    getBlobReferenceCount: builder.query({
        query: ({ projectId, packageId, status }) => {
            const params = createParams({ status });
            return {
                url: `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/blobs/reference-count/`,
                method: 'GET',
                params: mapToSnakeCase(params),
            };
        },
        providesTags: ['Blobs'],
        transformResponse: createTransformer(getBlobReferenceCountResponseSchema, 'GetBlobReferenceCount'),
    }),
    getParsedDataByGuidAndContent: builder.query({
        query: ({ projectId, packageId, guid, content }) => `${coreApiBaseUrl}/projects/${projectId}/packages/${packageId}/parsed-data/?guid=${guid}&content=${content}`,
        transformResponse: createTransformer(parsedDataResponseSchema, 'SearchParsedDataByContent'),
    }),
});
